/**
 * Perfect-phrase streak badge.
 *
 * A row of `n` round checkboxes anchored to the top-right of the loop bracket
 * overlay, where `n` is the qualifying streak (target) sourced from React/Redux.
 * The boxes start empty; each consecutive perfect loop fills one box with the
 * green `checkmark` sprite. When all `n` are filled the section is "perfected".
 *
 * The scene owns one instance: it sets the target (number of boxes) from the
 * PPC config, updates the streak as the controller reports it, positions the
 * row each frame from the bracket geometry, and hides it when the counter is
 * off or looping stops.
 */

import Phaser from 'phaser';

const BOX_RADIUS = 23; // ~1.75× the original 13px radius
const BOX_SPACING = 56; // centre-to-centre between adjacent checkboxes (~1.75×)
// The checkmark.svg is a green disc (r=45 in a 96×96 viewBox) with a white tick,
// so the visible disc only fills 90/96 of the texture's bounding box. Inflate
// the display size by the inverse of that ratio so the green disc fully covers
// the placeholder slot circle (diameter = BOX_RADIUS * 2) rather than sitting
// slightly inside it.
const CHECK_GLYPH_RATIO = 90 / 96;
const CHECK_DISPLAY_SIZE = (BOX_RADIUS * 2) / CHECK_GLYPH_RATIO;
const EMPTY_FILL_COLOR = 0xced4da; // light grey for an unchecked box
const BORDER_COLOR = 0xffffff;
const BORDER_WIDTH = 2;
const BADGE_DEPTH = 95; // above the bracket overlay (90–92)

interface Slot {
  box: Phaser.GameObjects.Graphics;
  check: Phaser.GameObjects.Image;
}

export default class PerfectPhraseBadge {
  private readonly scene: Phaser.Scene;
  private readonly container: Phaser.GameObjects.Container;
  private slots: Slot[] = [];
  private target = 0;
  private streak = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setDepth(BADGE_DEPTH);
    this.container.setVisible(false);
  }

  /**
   * Set how many checkboxes to render (the qualifying streak size). Rebuilds
   * the row and re-applies the current streak. No-op when unchanged.
   */
  setTarget(target: number): void {
    if (target === this.target) return;
    this.target = Math.max(0, target);
    this.rebuild();
    this.applyStreak(false);
  }

  /** Update the streak. Fills/empties checkboxes; pops a newly-filled box. */
  setStreak(streak: number): void {
    if (streak === this.streak) return;
    const increased = streak > this.streak;
    this.streak = streak;
    this.applyStreak(increased);
  }

  /** Position the row centre in world space (top-right of the bracket). The
   *  rightmost checkbox sits at this anchor; the row extends to the left. */
  setWorldPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  /** Show/hide. The scene hides this when the counter is off or not looping. */
  setVisible(visible: boolean): void {
    this.container.setVisible(visible && this.target > 0);
  }

  /** Rebuild the checkbox row for the current target count. */
  private rebuild(): void {
    this.slots.forEach(({ box, check }) => {
      box.destroy();
      check.destroy();
    });
    this.slots = [];

    const n = this.target;
    for (let i = 0; i < n; i++) {
      // Right-align the row at the container origin: the rightmost box (i=n-1)
      // sits at x=0, earlier boxes extend to the left.
      const x = -(n - 1 - i) * BOX_SPACING;

      const box = this.scene.add.graphics();
      box.fillStyle(EMPTY_FILL_COLOR, 1);
      box.lineStyle(BORDER_WIDTH, BORDER_COLOR, 1);
      box.fillCircle(x, 0, BOX_RADIUS);
      box.strokeCircle(x, 0, BOX_RADIUS);

      const check = this.scene.add.image(x, 0, 'checkmark').setVisible(false);
      check.setDisplaySize(CHECK_DISPLAY_SIZE, CHECK_DISPLAY_SIZE);

      this.container.add([box, check]);
      this.slots.push({ box, check });
    }
  }

  /**
   * Reflect the current streak across the checkboxes. Box `i` is filled when
   * `i < streak`. When `animateNew` is set, the box that just became filled
   * pops. The streak display is naturally capped at the target by the slot
   * count.
   */
  private applyStreak(animateNew: boolean): void {
    this.slots.forEach((slot, i) => {
      const checked = i < this.streak;
      if (checked === slot.check.visible) return;
      slot.check.setVisible(checked);
      // The green checkmark disc fully covers the slot, so drop the placeholder
      // circle while checked (and restore it when the streak resets).
      slot.box.setVisible(!checked);
      if (checked && animateNew && i === this.streak - 1) {
        this.pop(slot.check);
      }
    });
  }

  /** A brief scale-up pop when a checkbox is newly filled. Tweens relative to
   *  the sprite's fitted scale (set via setDisplaySize) so it settles back to
   *  the correct box size rather than the texture's native scale. */
  private pop(target: Phaser.GameObjects.Image): void {
    const baseX = target.scaleX;
    const baseY = target.scaleY;
    this.scene.tweens.add({
      targets: target,
      scaleX: { from: baseX * 1.6, to: baseX },
      scaleY: { from: baseY * 1.6, to: baseY },
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  destroy(): void {
    this.container.destroy();
  }
}
